import { Deserializer } from 'jsonapi-serializer';
import { TreeHierarchy } from '../components/checkbox-tree/checkboxNodeField';
import Commands from '../constants/commands';
import { CREATED, INTERNAL_SERVER_ERROR, NO_CONTENT } from '../constants/response-status-codes';
import { HardwareSubCmds } from '../constants/sub-commands';
import {Controller, ControllerCerts} from '../models/Controller';
import { ControllerName } from "../models/ControllerName";
import { genericError } from '../models/GenericError';
import { ListQuery } from '../models/ListQuery';
import { Name } from '../models/Name';
import { PaginationModel } from '../models/Pagination';
import { StoreEntityResult } from '../models/services/StoreEntityResult';
import { ControllerWrapper } from "../models/wrappers/ControllerWrapper";
import { APIConnector } from './api/APIConnector';
import { updateNetworkSettings } from './networkSettingsService';
import { getConstraintViolation, processServerError } from './utilities/ServiceError';
import { getDeviceLicenceStatus } from "./utilities/StatusBuilder";
import { createPagination } from "./utilities/paginationBuilder";
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';
import { accessAreaBranches } from '@/constants/area-constants';

const apiConnector = new APIConnector();

const buildController = (controller): Controller => {
    return {
        id: controller['id'],
        name: controller['dev-name'],
        fixedAddress: controller['dev-fixed-addr'],
        serial: controller['dev-serial'],
        ipAddress: controller['dev-ip-address'],
        wanIpAddress: controller['wan-ip-address'],
        sla: controller['dev-sla'],
        fwVersion: controller['fw-version'],
        enabled: controller['dev-enabled'],
        port: controller['dev-port'],
        site: controller['site']['site-name'],
        siteId: controller['site']['id'],
        readers: controller['total-readers'],
        zones: controller['total-zones'],
        preset: controller['profile']['trn-name'],
        presetId: controller['profile']['id'],
        type: controller['unit-type']['trn-name'],
        typeNumber: controller['unit-type']['ut-no'],
        typeId: controller['unit-type']['id'],
        description: controller['dev-desc'] ?? '',
        totalActions: controller['total-actions'],
        totalDoorModePatterns: controller['total-door-mode-patterns'],
        totalInputs: controller['total-inputs'],
        totalParameters: controller['total-parameters'],
        version: controller["fw-version"],
        macAddress: controller["discoveries"][0]["mac-address"],
        heartBeatReporting: controller["heartbeat-reporting"],
        buildingId: controller["building"] ? controller["building"]['id'] : undefined,
        buildingName: controller["building"] ? controller["building"]['building-name'] : "",
        floorId: controller["floor"] ? controller["floor"]['id'] : "",
        floorName: controller["floor"] ? controller["floor"]['floor-name'] : "",
        controllerId: controller["id"],
        forceChanges: controller["discoveries"][0]['force-changes'],
        staticIpAddress: controller["discoveries"][0]['ip-address-static'],
        safeIp: controller["discoveries"][0]['safe-ip'],
        hostname: controller["discoveries"][0]['host-name'],
        gateway: controller["discoveries"][0]['gateway'],
        incomingPortFromDC: controller["discoveries"][0]['term-to-ctrl-port'],
        incomingPortFromEngine: controller["discoveries"][0]['host-to-ctrl-port'],
        outgoingPortFromEngine: controller["discoveries"][0]['host-port'],
        subnetMask: controller["discoveries"][0]['netmask'],
        discoveryId: controller["discoveries"][0]['id'],
        queue: 0,
        status: getDeviceLicenceStatus(true, controller['dev-enabled'], controller['dev-comms-status']),
        positionKey: controller['dev-direction'],
        updateType: "0",
        isGatewayStatic: controller["discoveries"][0]['gateway-static'],
        isNetmaskStatic: controller["discoveries"][0]['netmask-static'],
        synchroniseChangesToHardware: false,
        nameOriginal: controller['dev-name'],
        isDefaultPassword :controller["discoveries"][0]['is-default-password'],
        passwordState :controller["discoveries"][0]['password-state'],
        certificates: controller['ks-certificates'] ? controller['ks-certificates'].map((cert): ControllerCerts => {
            return {
                ksCertificateId: cert['id'],
                ksCertificateName: cert['name'],
                ksCertificateStatus: cert['ks-certificate-status']['trn-name'],
                ksCertificateStatusId: cert['ks-certificate-status']['id']
            };
        }) : [],
        tlsMode: controller["discoveries"][0]['tls-mode'],
        tlsPort: controller["discoveries"][0]['tls-port'],
        tlsUrl: controller["discoveries"][0]['tls-url'],
    };
};

export const getControllers = async (listParameters?: ListQuery): Promise<ControllerWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const unitTypeFields = "fields[unitType]=trnName";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName";
        const discoveryField = "fields[discovery]=passwordState";
        const controllerFields = "fields[controller]=devName,devFixedAddr,fwVersion,devSerial,devIpAddress,devEnabled,devCommsStatus,site,profile,unitType,discoveries,deviceImprox";
        const include = "include=unitType,profile,site,discoveries,deviceImprox";
        const url = `controller?${include}&${unitTypeFields}&${siteFields}&${profileFields}&${controllerFields}&${discoveryField}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const controllers: Controller[] = data.map(buildController);
                return resolve({ rows: controllers, pagination })
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getControllerById = async (controllerId: string): Promise<Controller> => {
    try {
        const unitTypeFields = "fields[unitType]=trnName,utNo";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName";
        const buildingFields = "fields[building]=buildingName";
        const floorFields = "fields[floor]=floorName";
        const controllerFields = "fields[controller]=totalReaders,totalZones,heartbeatReporting,devName,devDirection,devFixedAddr,devSerial,devIpAddress,wanIpAddress,devSla,devDesc,fwVersion,devEnabled,devPort,totalActions,totalDoorModePatterns,totalInputs,totalParameters,devEnabled,devCommsStatus,site,profile,unitType,building,floor,discoveries,deviceImprox,ksCertificates";
        const zoneFields = "fields[zone]=zoneName";
        const discoveryField = "fields[discovery]=macAddress,forceChanges,ipAddressStatic,safeIp,hostName,gateway,hostToCtrlPort,termToCtrlPort,hostPort,netmask,gatewayStatic,netmaskStatic,wanIpAddress,tlsMode,tlsPort,tlsUrl,isDefaultPassword,passwordState";
        const certificateFields = "fields[ksCertificate]=name,ksCertificateStatus";
        const certificateStatusFields = "fields[ksCertificateStatus]=trnName";
        const include = "include=unitType,building,floor,profile,site,discoveries,deviceImprox,ksCertificates,ksCertificates.ksCertificateStatus";
        const url = `controller/${controllerId}?${include}&${unitTypeFields}&${siteFields}&${profileFields}&${buildingFields}&${floorFields}&${controllerFields}&${zoneFields}&${discoveryField}&${certificateFields}&${certificateStatusFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const controller: Controller = buildController(data);
                return resolve(controller)
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getControllerNames = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `controller?include=deviceImprox&fields[controller]=deviceImprox,devName${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta)

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const controllers: Name[] = items.map((controller): Name => {
                    return {
                        value: controller['id'],
                        label: controller['dev-name']
                    };
                });

                return resolve({ rows: controllers, pagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getControllerNamesWithPagnation = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const url = `controller?include=deviceImprox,site&fields[site]=siteName&fields[controller]=deviceImprox,site,devName&${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const controllers: ControllerName[] = items.map((controller): ControllerName => {
                    return {
                        value: controller['id'],
                        label: controller['dev-name'],
                        optionalColumns: [{ key: 'site', value: controller["site"]["site-name"] || '-' }],
                    };
                });

                return resolve({ rows: controllers, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const createListRelationship = (id, typeName) => {
    return {
        type: typeName,
        id: id
    }
}

const createRelationship = (id, typeName) => {
    return {
        data: {
            type: typeName,
            id: id
        }
    }
}

export const storeController = async (controller: Controller, createController = true, oldIpAddress: string) => {
    try {
        const newRecord = {
            data: {
                type: 'controller',
                id: controller.id,
                attributes: {
                    devDesc: controller.description,
                    devEnabled: !!controller.enabled,
                    devName: controller.name,
                    devIpAddress: controller.ipAddress,
                    devPort: controller.port,
                    wanIpAddress: controller.wanIpAddress,
                    heartbeatReporting: controller.heartBeatReporting
                },
                relationships: {
                    profile: {
                        data: {
                            type: "profile",
                            id: controller.presetId
                        }
                    }
                }
            }
        }

        newRecord.data.relationships['building'] = controller.buildingId ? createRelationship(controller.buildingId, 'building') : {};
        newRecord.data.relationships['floor'] = controller.floorId ? createRelationship(controller.floorId, 'floor') : {};
        if (controller.certificates && controller.certificates.length > 0) {
            newRecord.data.relationships['ksCertificates'] = {
                data: controller.certificates.map(cert => createListRelationship(cert.ksCertificateId, 'ksCertificate'))
            };
        }

        const url = `controller${createController ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        /**
            0 = Update Device Only
            1 = Update Device and Comms Channel

            Update Device Only update discovery table only
            Update Device and Comms Channel update both discovery and controller
         */
        await apiConnector.sendData(url, createController, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            })
        });

        try {
            await updateNetworkSettings(controller, oldIpAddress);
        } catch (error) {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            });
        }

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createController) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }
                    new Deserializer().deserialize(content, (error, newController) => {
                        if (error) {
                            return reject({ status: response.status, id: newController.id, success: false, message: genericError });
                        }
                        return resolve({ id: newController.id, success: true });
                    })
                    return;
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: controller.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: controller.id, success: false, message: getConstraintViolation(content.errors[0].detail) });
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: controller.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const getUpdatedControllerInfo = (controller) => {
    const newController = {
        data: {
            type: "controller",
            id: controller.id,
            attributes: {
                heartbeatReporting: controller.heartBeatReporting,
            }
        }
    }

    return newController;
}

const getDiscoveryInfo = (controller: Controller) => {
    return {
        data: {
            type: "discovery",
            id: controller.discoveryId,
            attributes: {
                forceChanges: controller.forceChanges ? 1 : 0,
                gateway: controller.gateway,
                hostName: controller.hostname,
                hostPort: controller.outgoingPortFromEngine,
                hostToCtrlPort: controller.incomingPortFromEngine,
                ipAddress: controller.ipAddress,
                ipAddressStatic: controller.staticIpAddress ? 1 : 0,
                netmask: controller.subnetMask,
                safeIp: controller.safeIp ? 1 : 0,
                termToCtrlPort: controller.incomingPortFromDC,
                gatewayStatic: controller.isGatewayStatic ? 1 : 0,
                netmaskStatic: controller.isNetmaskStatic ? 1 : 0,
                synchroniseChangesToHardware: controller.synchroniseChangesToHardware,
                updateType: controller.updateType,
                wanIpAddress: controller.wanIpAddress
            }
        }
    }
}

export const deleteController = async (userLoginId: string, fixedAddress: string) => {
    try {
        const syncData = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: Commands.Engine.label,
                    cmdSubType: HardwareSubCmds.Uninstall_Controller,
                    userLoginId: userLoginId,
                    fixedAddress: fixedAddress,
                    jsonResponse: ""
                }
            }
        }

        let response: Response;

        await apiConnector.sendData("executeCommand", true, syncData).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject({ status: response.status, message: genericError })
            });
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response.status === CREATED) {
                return resolve({ id: 1, success: true })
            }
            const json = await response.json();

            return reject({ status: response.status, id: 1, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const savePassword = async (newPassword: string, discoveryId: string, password?: string, resync = false) => {
    try {
        const record = {
            data: {
                type: "discovery",
                id: discoveryId,
                attributes: {
                    newPassword: newPassword,
                    onlyUpdatingPassword: true,
                    resyncWithHardware: resync
                }
            }
        }

        if (password) {
            record.data.attributes['decodedPassword'] = password;
        }

        const url = `discovery/${discoveryId}`;
        let response: Response;

        await apiConnector.sendData(url, false, record).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (response.status === NO_CONTENT) {
                    return resolve({ id: record.data.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: record.data, success: false, message: getConstraintViolation(content.errors[0].detail) });
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: record.data.id, success: false, message: genericError });
        })

    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const assigntController = async (
    controllerPresetId: string,
    controllerPresetIdsToAdd: string[]
) => {

    try {
        const controllerPresetToAdd = controllerPresetIdsToAdd.map(
            (controllerId) => ({
                data: {
                    type: "deviceImprox",
                    id: controllerId,
                    relationships: {
                        profile: {
                            data: {
                                type: "profile",
                                id: controllerPresetId,
                            },
                        },
                    },
                },
            })
        );

        const addedControllers = controllerPresetToAdd.map((reader) =>
            apiConnector.sendData(
                `deviceImprox/${reader.data.id}`,
                false,
                reader
            )
        );

        let reposnedata = await Promise.all(addedControllers);

            const hasFailedResponse = reposnedata.some(
                (element) => element?.ok === false
            );

            if (hasFailedResponse) {
                Promise.reject({ success: false });
            }
        return Promise.resolve({ success: true });

    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};


/**
    This shouldn't be here, it should be moved to accessArea.tsx service should only be doing API calls
 */

const buildControllerBranches = (areaId, controller): TreeHierarchy => {
    let checked = false;
    const areaCounts = controller['area-location-counts']?.find(areaCountLocation => areaCountLocation['area-id'].toString() == areaId);

    if (areaCounts) {
        checked = areaCounts.count > 0;
    }

    return ({
        id: controller.id,
        name: controller['dev-name'],
        label: controller['dev-name'],
        options: [],
        checked: checked,
        branch: accessAreaBranches.CONTROLLER
    });
}

export const getControllerTree = async (listParameters?: ListQuery,areaId?): Promise<{ rows: TreeHierarchy[], pagination: PaginationModel,controllerZonesObjs:{[key:string]:string[]} }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const contollerFields = "fields[controller]=devName,areaLocationCounts,zones";
        const url = `controller?${contollerFields}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta)

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }
                // group zones by controllers
                const controllerZonesObjs = json.data.reduce((acc, controller) => {
                    acc[controller.id]= controller.relationships.zones.data.map(zone => zone.id);
                    return acc;
                },{});
                const controllers:TreeHierarchy[] = data.map((controller) => buildControllerBranches(areaId, controller));

                return resolve({ rows: controllers, pagination, controllerZonesObjs });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
